// Copyright (c) 2026 Michael F. Kamprath
//
// home-mascot.js -- animated sheep for the home screen.
//
// Idle behavior: the sheep sits in the head-up static pose
// (ewe_static_img.webp -- same resting pose used in the sheep scene),
// and periodically runs a full graze cycle -- head lowers via the
// raises_head transition frames, munches through the eats frames,
// then raises its head back up.  All three phases + their timings
// mirror animations.json sheep.idle {transitionToIdle, idleAnimation,
// transitionFromIdle}.  A subtle eyelid blink also plays at random
// intervals while the sheep is in the static head-up pose.  Every
// motion is suppressed under prefers-reduced-motion.
//
// Tap reaction: 50/50 random between
//   (a) eweRock     -- ewe_rocking_01 and 02 alternating over 1.2 s,
//                      no sound, no transforms.  Matches the legacy
//                      iOS home-screen touchReaction (sheep.touchReactions
//                      in animations.json -> ["eweRock"]).
//   (b) jump+baa    -- ewe_jump_01..05 matching the timings in
//                      animations.json for sheep.cycles.jump.standard,
//                      with rotate(+30deg -> -30deg) and translateY(0 ->
//                      ~-30% -> 0) applied via CSS @keyframes on the
//                      transform wrapper.  sheep_baa_jump.mp3 fires at
//                      t = 150 ms, matching the soundFile keyframe in
//                      the legacy spec.  No horizontal displacement.
//
// Tap is disabled while a reaction is in flight.  On destroy, all
// timers are cleared and the DOM is detached.

import { trackUiAction } from '../telemetry.js';

const SHEEP_BAA_URL = '/audio/sfx/sheep_baa_jump.mp3';

// Frame URLs keyed by a short identifier.  All 16 frames are preloaded
// at mount time via the browser's <img> loader -- not via the Howler /
// Pixi asset loaders -- because these are plain DOM images, not game
// sprites, and come out of the vite-plugin-pwa precache after the
// first visit regardless.
const FRAMES = Object.freeze({
  static:        '/images/sheep/ewe_static_img.webp',
  staticBlink:   '/images/sheep/ewe_static_eyelids.webp',
  eats01:        '/images/sheep/ewe_eats_01.webp',
  eats02:        '/images/sheep/ewe_eats_02.webp',
  eats03:        '/images/sheep/ewe_eats_03.webp',
  eats04:        '/images/sheep/ewe_eats_04.webp',
  raisesHead01:  '/images/sheep/ewe_raises_head_01.webp',
  raisesHead02:  '/images/sheep/ewe_raises_head_02.webp',
  raisesHead03:  '/images/sheep/ewe_raises_head_03.webp',
  raisesHead04:  '/images/sheep/ewe_raises_head_04.webp',
  rock01:        '/images/sheep/ewe_rocking_01.webp',
  rock02:        '/images/sheep/ewe_rocking_02.webp',
  jump01:        '/images/sheep/ewe_jump_01.webp',
  jump02:        '/images/sheep/ewe_jump_02.webp',
  jump03:        '/images/sheep/ewe_jump_03.webp',
  jump04:        '/images/sheep/ewe_jump_04.webp',
  jump05:        '/images/sheep/ewe_jump_05.webp',
});

/** The default resting pose -- sheep standing, head up.  Matches
 *  themes.json sheep.sprites.static (same pose the in-game sheep
 *  returns to when the scene tears down). */
const IDLE_FRAME_KEY = 'static';

// --- Graze cycle phases -------------------------------------------------
//
// The graze cycle has three phases concatenated into one absolute-
// millisecond timeline:
//
//   1. transitionToIdle   (400 ms) raises_head_04..01, head lowers
//   2. munch (N cycles)   each   800 ms of eats_01..04, head down
//   3. transitionFromIdle (400 ms) raises_head_01..04, head rises
//
// Per-frame timings come from animations.json sheep.idle, converted
// seconds -> ms.  Phase 2 can run multiple munch cycles back-to-back
// based on grazeMunchDurationMs (config): num cycles =
// max(1, round(munchMs / MUNCH_CYCLE_MS)).  Default 800 ms = a
// single cycle (the original behaviour); setting higher extends the
// head-down time.  The gap BETWEEN graze cycles is a separate
// tunable (grazeMinGapMs / grazeMaxGapMs).
const MUNCH_CYCLE_MS = 800;
const DEFAULT_GRAZE_MUNCH_DURATION_MS = 800;
const TRANSITION_TO_IDLE_MS = 400;
const TRANSITION_FROM_IDLE_MS = 400;

function buildGrazeSequence(grazeMunchDurationMs) {
  const numCycles = Math.max(1, Math.round(grazeMunchDurationMs / MUNCH_CYCLE_MS));
  const seq = [];
  // Phase 1: head lowers.
  seq.push({ frameKey: 'raisesHead04', at: 0   });
  seq.push({ frameKey: 'raisesHead03', at: 100 });
  seq.push({ frameKey: 'raisesHead02', at: 200 });
  seq.push({ frameKey: 'raisesHead01', at: 300 });
  // Phase 2: one or more munch cycles (eats_01..04 timings are
  // non-uniform -- 01 held for 400 ms, 02 for 200 ms, 03 for 100 ms,
  // 04 for 100 ms = 800 ms total per cycle).
  for (let i = 0; i < numCycles; i++) {
    const base = TRANSITION_TO_IDLE_MS + i * MUNCH_CYCLE_MS;
    seq.push({ frameKey: 'eats01', at: base       });
    seq.push({ frameKey: 'eats02', at: base + 400 });
    seq.push({ frameKey: 'eats03', at: base + 600 });
    seq.push({ frameKey: 'eats04', at: base + 700 });
  }
  // Phase 3: head rises.
  const phase3Start = TRANSITION_TO_IDLE_MS + numCycles * MUNCH_CYCLE_MS;
  seq.push({ frameKey: 'raisesHead01', at: phase3Start       });
  seq.push({ frameKey: 'raisesHead02', at: phase3Start + 100 });
  seq.push({ frameKey: 'raisesHead03', at: phase3Start + 200 });
  seq.push({ frameKey: 'raisesHead04', at: phase3Start + 300 });
  const totalMs = phase3Start + TRANSITION_FROM_IDLE_MS;
  return { sequence: seq, totalMs };
}

// --- Built-in defaults for tunable fields --------------------------------
// Mirrors src/app-config.js DEFAULTS.home.mascot.  When a caller passes
// a `config` opt (via app.js wiring loadAppConfig() -> getHomeMascotConfig()),
// those values override these; otherwise the mascot runs stand-alone
// with these built-ins so unit tests don't need to stub the whole
// config loader.
const DEFAULT_GRAZE_MIN_GAP_MS = 6000;
const DEFAULT_GRAZE_MAX_GAP_MS = 10000;
const DEFAULT_BLINK_MIN_GAP_MS = 3000;
const DEFAULT_BLINK_MAX_GAP_MS = 5000;
const DEFAULT_BLINK_DURATION_MS = 140;
const DEFAULT_ROCK_PROBABILITY = 0.5;

// --- Rock reaction (1.2 s, silent) ---------------------------------------
const ROCK_SEQUENCE = Object.freeze([
  { frameKey: 'rock01', at: 0    },
  { frameKey: 'rock02', at: 200  },
  { frameKey: 'rock01', at: 400  },
  { frameKey: 'rock02', at: 600  },
  { frameKey: 'rock01', at: 800  },
  { frameKey: 'rock02', at: 1000 },
]);
const ROCK_DURATION_MS = 1200;

// --- Jump+baa reaction (~1.7 s, y-translate + rotation) ------------------
// Frame + transform + sound timings mirror sheep.cycles.jump.standard in
// animations.json, converted from seconds to milliseconds.  The CSS
// @keyframes block in home.css drives transform (rotation + translateY)
// synchronised to the same duration -- JS ticks the body frames and
// fires the baa at t=150 ms.
const JUMP_SEQUENCE = Object.freeze([
  { frameKey: 'jump01', at: 0    },
  { frameKey: 'jump02', at: 150  },  // sound keyframe lands here
  { frameKey: 'jump03', at: 300  },
  { frameKey: 'jump04', at: 1300 },
  { frameKey: 'jump05', at: 1450 },
]);
const JUMP_DURATION_MS = 1700;
const JUMP_BAA_AT_MS = 150;

/**
 * @typedef {Object} HomeMascotHandle
 * @property {HTMLElement} element   The root div; caller appends it.
 * @property {() => void} destroy    Tear down all timers + detach.
 * @property {() => string} __getState    Test hook: 'idle' | 'chewing' | 'reacting'.
 * @property {() => string} __getFrame    Test hook: active frame key.
 * @property {() => void} __triggerTap    Test hook: synthesise a tap.
 * @property {() => boolean} __isReducedMotion  Test hook.
 */

/**
 * Build the home-screen mascot.  Caller mounts `element` into the DOM
 * (typically inside .home-screen, between title and nav).  The
 * mascot starts its idle cycle on construction and runs until
 * destroy() is called.
 *
 * @param {{
 *   playSheepBaa?: (src: string) => Promise<void>,
 *     Callback for the baa sound on jump+baa.  Defaults to a no-op so
 *     tests that don't care about audio can skip wiring it.
 *   randomFn?: () => number,
 *     Stub point for test-deterministic reaction + timer randomisation.
 *     Defaults to Math.random.
 *   reducedMotion?: boolean,
 *     Explicit override for prefers-reduced-motion detection.  Tests
 *     pass `true` to force the static-only branch; production reads
 *     the media query.
 *   setTimeoutImpl?: typeof setTimeout,
 *   clearTimeoutImpl?: typeof clearTimeout,
 *     Injectable clock for tests.
 *   config?: {
 *     grazeMinGapMs?: number, grazeMaxGapMs?: number,
 *     blinkMinGapMs?: number, blinkMaxGapMs?: number,
 *     blinkDurationMs?: number,
 *     rockProbability?: number,
 *     width?: string | null, offsetX?: string | null,
 *     offsetY?: string | null, jumpPeakY?: string | null,
 *   }
 *     Per-field overrides for tap-reaction mix, idle cadence, and
 *     placement.  Typically built by app.js via
 *     getHomeMascotConfig(loadAppConfig()) so the values come from
 *     public/app-config.json.  Unset fields use the built-in
 *     defaults above; null string fields defer to the stylesheet.
 * }} [opts]
 * @returns {HomeMascotHandle}
 */
export function createHomeMascot(opts = {}) {
  const playSheepBaa = typeof opts.playSheepBaa === 'function'
    ? opts.playSheepBaa
    : () => Promise.resolve();
  const randomFn = typeof opts.randomFn === 'function'
    ? opts.randomFn
    : Math.random;
  const setTimeoutImpl = typeof opts.setTimeoutImpl === 'function'
    ? opts.setTimeoutImpl
    : ((fn, ms) => setTimeout(fn, ms));
  const clearTimeoutImpl = typeof opts.clearTimeoutImpl === 'function'
    ? opts.clearTimeoutImpl
    : ((t) => clearTimeout(t));
  const reducedMotion = typeof opts.reducedMotion === 'boolean'
    ? opts.reducedMotion
    : detectReducedMotion();

  // --- Resolve tunable config (from app-config.json or built-in defaults) --
  // Fields can be updated post-mount via the returned setConfig()
  // handle -- app.js calls that once the async loadAppConfig() resolves.
  const finiteOr = (v, fb) => (Number.isFinite(v) && v >= 0 ? v : fb);
  const probOr = (v, fb) => (Number.isFinite(v) && v >= 0 && v <= 1 ? v : fb);
  const strOr  = (v) => (typeof v === 'string' && v.length > 0 ? v : null);
  let grazeMinGapMs        = DEFAULT_GRAZE_MIN_GAP_MS;
  let grazeMaxGapMs        = DEFAULT_GRAZE_MAX_GAP_MS;
  let grazeMunchDurationMs = DEFAULT_GRAZE_MUNCH_DURATION_MS;
  let blinkMinGapMs        = DEFAULT_BLINK_MIN_GAP_MS;
  let blinkMaxGapMs        = DEFAULT_BLINK_MAX_GAP_MS;
  let blinkDurationMs      = DEFAULT_BLINK_DURATION_MS;
  let rockProbability      = DEFAULT_ROCK_PROBABILITY;

  function applyConfig(config) {
    const c = (config && typeof config === 'object') ? config : {};
    grazeMinGapMs        = finiteOr(c.grazeMinGapMs,        DEFAULT_GRAZE_MIN_GAP_MS);
    grazeMaxGapMs        = finiteOr(c.grazeMaxGapMs,        DEFAULT_GRAZE_MAX_GAP_MS);
    grazeMunchDurationMs = finiteOr(c.grazeMunchDurationMs, DEFAULT_GRAZE_MUNCH_DURATION_MS);
    blinkMinGapMs        = finiteOr(c.blinkMinGapMs,        DEFAULT_BLINK_MIN_GAP_MS);
    blinkMaxGapMs        = finiteOr(c.blinkMaxGapMs,        DEFAULT_BLINK_MAX_GAP_MS);
    blinkDurationMs      = finiteOr(c.blinkDurationMs,      DEFAULT_BLINK_DURATION_MS);
    rockProbability      = probOr (c.rockProbability,       DEFAULT_ROCK_PROBABILITY);
  }

  applyConfig(opts.config);

  // Style (placement + size) is applied AFTER the DOM element exists --
  // see the scaffold section below.  `styleWidth` etc. are resolved
  // here so both the initial apply and subsequent setConfig() calls
  // share the same nullish-string handling.
  let styleWidth    = strOr(opts.config?.width);
  let styleOffsetX  = strOr(opts.config?.offsetX);
  let styleOffsetY  = strOr(opts.config?.offsetY);
  let styleJumpPeak = strOr(opts.config?.jumpPeakY);

  // --- DOM scaffold ------------------------------------------------------
  const el = document.createElement('div');
  el.className = 'home-screen__mascot';
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', 'A friendly sheep');
  function applyStyleConfig() {
    // Apply placement + size overrides via CSS custom properties.  The
    // stylesheet reads these with fallbacks, so `null` on either side
    // means "defer to the stylesheet default" and we remove the prop.
    const set = (name, val) => {
      if (val !== null) el.style.setProperty(name, val);
      else el.style.removeProperty(name);
    };
    set('--home-mascot-width',       styleWidth);
    set('--home-mascot-offset-x',    styleOffsetX);
    set('--home-mascot-offset-y',    styleOffsetY);
    set('--home-mascot-jump-peak-y', styleJumpPeak);
  }
  applyStyleConfig();

  // Grass patch: purely decorative ground under the sheep.  Lives
  // OUTSIDE the transformer so it stays anchored when the sheep
  // rotates + translates during a jump.  Rendered entirely in CSS --
  // no asset file.  Colour is tunable via --home-mascot-ground-color.
  const ground = document.createElement('div');
  ground.className = 'home-screen__mascot__ground';
  ground.setAttribute('aria-hidden', 'true');
  el.appendChild(ground);

  // Transformer sits between el and the frame imgs.  CSS applies the
  // jump keyframes (translateY + rotate) to this element while the
  // frame swaps happen inside it.  Keeps rotation about the body
  // centre rather than the outer bounding box.
  const transformer = document.createElement('div');
  transformer.className = 'home-screen__mascot__transformer';
  el.appendChild(transformer);

  /** @type {Map<string, HTMLImageElement>} */
  const frameEls = new Map();
  for (const [key, url] of Object.entries(FRAMES)) {
    const img = document.createElement('img');
    img.className = 'home-screen__mascot__frame';
    if (key === 'staticBlink') {
      img.classList.add('home-screen__mascot__frame--blink');
    }
    img.src = url;
    img.alt = '';
    img.draggable = false;
    img.decoding = 'async';
    transformer.appendChild(img);
    frameEls.set(key, img);
  }

  // Initial visible frame: the idle graze pose.
  frameEls.get(IDLE_FRAME_KEY).classList.add('home-screen__mascot__frame--visible');

  // --- State -------------------------------------------------------------
  /** @type {'idle' | 'grazing' | 'reacting'} */
  let state = 'idle';
  /** @type {string} */
  let activeFrameKey = IDLE_FRAME_KEY;
  /** @type {Set<any>} Active timer handles; cleared on destroy. */
  const timers = new Set();

  function setTimer(fn, ms) {
    const id = setTimeoutImpl(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
    return id;
  }

  function clearAllTimers() {
    for (const id of timers) clearTimeoutImpl(id);
    timers.clear();
  }

  function setFrame(key) {
    if (key === activeFrameKey) return;
    const prev = frameEls.get(activeFrameKey);
    const next = frameEls.get(key);
    if (prev) prev.classList.remove('home-screen__mascot__frame--visible');
    if (next) next.classList.add('home-screen__mascot__frame--visible');
    activeFrameKey = key;
  }

  function randomGap(min, max) {
    return min + randomFn() * (max - min);
  }

  // --- Idle loop ---------------------------------------------------------
  //
  // Two independent timers run while state === 'idle':
  //   * Graze timer: every 6-10 s, promote to 'grazing', run
  //                  GRAZE_SEQUENCE (head down -> munch -> head up, 1.6 s),
  //                  then fall back to 'idle' and reschedule.
  //   * Blink timer: every 3-5 s, overlay the static eyelids for
  //                  BLINK_DURATION_MS.
  //
  // Timers carry their own IDs so destroy can sweep the set.  Under
  // reduced-motion, neither is scheduled -- the sheep stays on the
  // static head-up pose.

  function scheduleNextGraze() {
    if (reducedMotion) return;
    setTimer(runGraze, randomGap(grazeMinGapMs, grazeMaxGapMs));
  }

  function runGraze() {
    // Guard: if a reaction started between the timer being set and
    // now, skip this graze and reschedule for later.
    if (state === 'reacting') {
      scheduleNextGraze();
      return;
    }
    state = 'grazing';
    const { sequence, totalMs } = buildGrazeSequence(grazeMunchDurationMs);
    for (const step of sequence) {
      if (step.at === 0) {
        setFrame(step.frameKey);
        continue;
      }
      setTimer(() => {
        // Guard: a tap may have promoted us mid-sequence.  If so, the
        // reaction's own frame setter owns the display; skip this step.
        if (state === 'grazing') setFrame(step.frameKey);
      }, step.at);
    }
    setTimer(() => {
      if (state === 'grazing') {
        state = 'idle';
        setFrame(IDLE_FRAME_KEY);
      }
      scheduleNextGraze();
    }, totalMs);
  }

  function scheduleNextBlink() {
    if (reducedMotion) return;
    setTimer(runBlink, randomGap(blinkMinGapMs, blinkMaxGapMs));
  }

  function hideBlinkOverlay() {
    // Idempotent -- removes the --visible class if present, so it's
    // safe to call from multiple paths (reaction start, destroy,
    // end-of-blink timer).  Prevents a mid-blink tap from leaving
    // the eyelid overlay stuck open while the reaction plays.
    const eyelid = frameEls.get('staticBlink');
    if (eyelid) eyelid.classList.remove('home-screen__mascot__frame--visible');
  }

  function runBlink() {
    // Only blink when on the static frame -- the eyelid overlay is
    // authored against ewe_static_img.webp and wouldn't align with
    // eats / raises_head / rocking / jump body frames.
    if (state !== 'idle' || activeFrameKey !== IDLE_FRAME_KEY) {
      scheduleNextBlink();
      return;
    }
    const eyelid = frameEls.get('staticBlink');
    eyelid.classList.add('home-screen__mascot__frame--visible');
    setTimer(() => {
      hideBlinkOverlay();
      scheduleNextBlink();
    }, blinkDurationMs);
  }

  // --- Tap reactions -----------------------------------------------------

  function runReaction(kind) {
    // Hard guard: tap during reaction is a no-op.  The DOM listener
    // also checks this, but double-guard because __triggerTap is a
    // test hook that could race with an in-flight reaction.
    if (state === 'reacting') return;
    state = 'reacting';

    // Force-hide the eyelid overlay BEFORE clearing timers.  If the
    // tap fires during the ~140 ms visible window of a blink, the
    // blink's own hide-timer is about to get cleared by
    // clearAllTimers(), which would leave the eyelid stuck open for
    // the full reaction duration.  Clearing the class here guarantees
    // the eyelid is hidden regardless of which blink phase we caught.
    hideBlinkOverlay();

    // Suspend graze/blink timers currently sitting on the set for the
    // reaction duration.  They'll be rescheduled when we return to idle.
    clearAllTimers();

    if (kind === 'rock') {
      runRock();
    } else {
      runJump();
    }
  }

  function runRock() {
    for (const step of ROCK_SEQUENCE) {
      if (step.at === 0) {
        // Synchronous first frame -- the tap should feel instant and
        // tests don't have to advance the clock by 0 to see it.
        setFrame(step.frameKey);
        continue;
      }
      setTimer(() => {
        if (state === 'reacting') setFrame(step.frameKey);
      }, step.at);
    }
    setTimer(finishReaction, ROCK_DURATION_MS);
  }

  function runJump() {
    el.classList.add('home-screen__mascot--jumping');
    for (const step of JUMP_SEQUENCE) {
      if (step.at === 0) {
        setFrame(step.frameKey);
        continue;
      }
      setTimer(() => {
        if (state === 'reacting') setFrame(step.frameKey);
      }, step.at);
    }
    // Fire the baa at the t=150 ms keyframe, matching the legacy
    // soundFile trigger in animations.json.  Wrap in a try/catch so a
    // playback error doesn't disrupt the animation loop.
    setTimer(() => {
      try {
        const p = playSheepBaa(SHEEP_BAA_URL);
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch { /* non-fatal */ }
    }, JUMP_BAA_AT_MS);
    setTimer(() => {
      el.classList.remove('home-screen__mascot--jumping');
      finishReaction();
    }, JUMP_DURATION_MS);
  }

  function finishReaction() {
    state = 'idle';
    setFrame(IDLE_FRAME_KEY);
    scheduleNextGraze();
    scheduleNextBlink();
  }

  function handleTap() {
    if (reducedMotion) return;
    if (state === 'reacting') return;
    // Telemetry: mascot_tap fires before we dispatch the reaction so
    // we count every landed tap regardless of whether the child then
    // spams taps during a reaction (those are blocked below as no-ops
    // and correctly NOT counted).
    try { trackUiAction({ action: 'mascot_tap' }); }
    catch { /* non-fatal */ }
    const kind = randomFn() < rockProbability ? 'rock' : 'jump';
    runReaction(kind);
  }

  el.addEventListener('click', handleTap);
  // Also respond to a real touch without waiting for the synthetic
  // click that follows 300ms later on older mobile browsers.  Modern
  // Safari / Chrome fire click fast enough that this is only a small
  // improvement, but it makes the reaction feel crisper on iPad.
  el.addEventListener('pointerdown', (ev) => {
    if (ev.pointerType === 'touch') handleTap();
  });
  // Keyboard a11y: Enter / Space on the focusable element counts as
  // a tap.  role=img + aria-label above make it a focusable landmark.
  el.setAttribute('tabindex', '0');
  el.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      handleTap();
    }
  });

  // --- Boot the idle cycle (unless reduced-motion) -----------------------
  scheduleNextGraze();
  scheduleNextBlink();

  // --- Cleanup -----------------------------------------------------------
  function destroy() {
    clearAllTimers();
    hideBlinkOverlay();
    if (el.parentNode) el.parentNode.removeChild(el);
  }

  /**
   * Update the mascot's tunables post-construction.  Called by app.js
   * once loadAppConfig() resolves, so the built-in defaults are in
   * effect for the ~1 frame between home-screen mount and config
   * fetch completion.  Numeric fields take effect on the NEXT
   * scheduled timer (in-flight timers keep their original duration);
   * style fields apply synchronously via CSS custom properties.
   */
  function setConfig(config) {
    applyConfig(config);
    styleWidth    = strOr(config?.width);
    styleOffsetX  = strOr(config?.offsetX);
    styleOffsetY  = strOr(config?.offsetY);
    styleJumpPeak = strOr(config?.jumpPeakY);
    applyStyleConfig();
  }

  return {
    element: el,
    destroy,
    setConfig,
    __getState() { return state; },
    __getFrame() { return activeFrameKey; },
    __triggerTap() { handleTap(); },
    __isReducedMotion() { return reducedMotion; },
  };
}

/**
 * Probe prefers-reduced-motion.  Falls back to `false` when
 * window.matchMedia isn't available (older browsers, jsdom without
 * a shim).  Returning false means "play the animations" -- the
 * conservative direction for CI where we also want to exercise the
 * animated code paths.
 */
function detectReducedMotion() {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
